import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch, apiUpload } from "@/lib/api/client"
import type {
  Agreement,
  AgreementDraftInput,
  EndAgreementInput,
  ApiAgreement,
  ApiFirmAgreements,
  FirmAgreements,
  UploadAgreementInput,
} from "@/types/agreement"
import type { ApiResource } from "@/lib/api/types"

export function toAgreement(data: ApiAgreement): Agreement {
  return {
    id: data.id,
    firm: { id: data.firm.id, legalName: data.firm.legal_name },
    source: data.source,
    status: data.status,
    standing: data.standing,
    validity: data.validity,
    title: data.title,
    periodLabel: data.period_label,
    summary: data.summary,
    watermark: data.watermark,
    signatureLabel: data.signature_label,
    notices: data.notices,
    actions: data.actions,
    templateVersion: data.template_version,
    body: data.body,
    contentHash: data.content_hash,
    sentAt: data.sent_at,
    effectiveFrom: data.effective_from,
    expiresOn: data.expires_on,
    signedOn: data.signed_on,
    signedAt: data.signed_at,
    signerName: data.signer_name,
    signerEmail: data.signer_email,
    signature: data.signature,
    signingDate: data.signing_date,
    signedCopy: data.signed_copy
      ? {
          id: data.signed_copy.id,
          filename: data.signed_copy.filename,
          downloadUrl: data.signed_copy.download_url,
        }
      : null,
    declinedAt: data.declined_at,
    declineReason: data.decline_reason,
    endedOn: data.ended_on,
    endReason: data.end_reason,
    createdAt: data.created_at,
  }
}

export const agreementQueries = {
  /** The one in play, the earlier ones, and how to get one while there is none. */
  forFirm: (firmId: string) =>
    queryOptions({
      queryKey: apiKey(`/firms/${firmId}/agreements`),
      queryFn: async ({ signal }): Promise<FirmAgreements> => {
        const { data } = await apiFetch<ApiResource<ApiFirmAgreements>>(
          `/firms/${firmId}/agreements`,
          { signal }
        )

        return {
          current: data.current ? toAgreement(data.current) : null,
          earlier: data.earlier.map(toAgreement),
          actions: data.actions,
        }
      },
    }),

  detail: (agreementId: string) =>
    queryOptions({
      queryKey: apiKey(`/agreements/${agreementId}`),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiAgreement>>(
          `/agreements/${agreementId}`,
          { signal }
        )

        return toAgreement(data)
      },
    }),
}

function store(queryClient: QueryClient, agreement: Agreement) {
  queryClient.setQueryData(apiKey(`/agreements/${agreement.id}`), agreement)
}

function draftPayload(input: AgreementDraftInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {}

  if (input.body !== undefined) {
    payload.body = input.body
  }
  if (input.effectiveFrom !== undefined) {
    payload.effective_from = input.effectiveFrom
  }
  if (input.expiresOn !== undefined) {
    payload.expires_on = input.expiresOn
  }

  return payload
}

/** A draft from the current template, filled in from the firm's record. */
export function useCreateAgreement(firmId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: Omit<AgreementDraftInput, "body"> = {}) => {
      const { data } = await apiFetch<ApiResource<ApiAgreement>>(
        `/firms/${firmId}/agreements`,
        { method: "POST", body: draftPayload(input) }
      )

      return toAgreement(data)
    },
    onSuccess: (agreement) => store(queryClient, agreement),
  })
}

export function useUpdateAgreement(agreementId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: AgreementDraftInput) => {
      const { data } = await apiFetch<ApiResource<ApiAgreement>>(
        `/agreements/${agreementId}`,
        { method: "PATCH", body: draftPayload(input) }
      )

      return toAgreement(data)
    },
    onSuccess: (agreement) => store(queryClient, agreement),
    scope: { id: `agreement:${agreementId}` },
  })
}

/** Freezes the wording and asks the firm's contact to sign it. */
export function useSaveAgreement(agreementId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiFetch<ApiResource<ApiAgreement>>(
        `/agreements/${agreementId}/save`,
        { method: "POST", body: {} }
      )

      return toAgreement(data)
    },
    onSuccess: (agreement) => store(queryClient, agreement),
    scope: { id: `agreement:${agreementId}` },
  })
}

/** A draft thrown away; the API names what that changed. */
export function useDiscardAgreement(agreementId: string) {
  return useMutation({
    mutationFn: async () => {
      await apiFetch<void>(`/agreements/${agreementId}`, { method: "DELETE" })
    },
    scope: { id: `agreement:${agreementId}` },
  })
}

/** An agreement signed outside Merrin, recorded as signed straight away. */
export function useUploadAgreement(firmId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: UploadAgreementInput) => {
      const body = new FormData()
      body.append("file", input.file)
      body.append("signed_on", input.signedOn)
      body.append("effective_from", input.effectiveFrom)
      body.append("expires_on", input.expiresOn)

      const { data } = await apiUpload<ApiResource<ApiAgreement>>(
        `/firms/${firmId}/agreements/upload`,
        { body }
      )

      return toAgreement(data)
    },
    onSuccess: (agreement) => store(queryClient, agreement),
  })
}

export function useEndAgreement(agreementId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ endedOn, reason }: EndAgreementInput) => {
      const { data } = await apiFetch<ApiResource<ApiAgreement>>(
        `/agreements/${agreementId}/end`,
        { method: "POST", body: { ended_on: endedOn, reason } }
      )

      return toAgreement(data)
    },
    onSuccess: (agreement) => store(queryClient, agreement),
  })
}
